import React from "react";
import InfoIcon from '@mui/icons-material/Info';
import { TableHead, TableCell, Table, TableRow, TableBody, TextField, MenuItem, Switch, Tooltip, Theme, createStyles, CardHeader } from "@mui/material";
import LockNotice from "../../Shared/LockNotice";
import { POSTED_APP_LOCK_TOOLTIP } from "../../../hooks/usePostedAppLock";
import { IEntityCustomField } from "../../../Interfaces/Application/IEntityCustomField";
import { ICustomValuePair } from "../../../Interfaces/Application/ICustomValuePair";
import { sort } from 'ramda';
import { useDispatch, useSelector } from "react-redux";
import { AppState } from '../../../store/rootReducer';
import { getApplicant, updateCustomerCustomFields } from "../../../utils/appUtils";
import { ApplicationDispatch } from "../../../store/application/types";
import { useBusinessRulesEngine } from "../../../Data/BusinessRulesData";
import { Constants } from "../../../utils/constants";


interface ICustomFieldFormProps {
  selectedCustomerId?: string;
  entityCustomFields?: IEntityCustomField[];
  onUpdateApplicationCustomField(field: ICustomValuePair): void;
  showScoreColumn: boolean;
  isBureauPage?: boolean;
  title?: string;
  /** When true, all custom fields are disabled because the application is in a locked status */
  lockedByPostStatus?: boolean;
  /** Why the fields are locked; the posted-application notice when not given. */
  lockedMessage?: string;
}

const CustomFieldForm: React.FC<ICustomFieldFormProps> = (props) => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const cobuyer1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
  const cobuyer2 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant2Id, true);
  const dispatch = useDispatch<ApplicationDispatch>();
  const businessRulesEngine = useBusinessRulesEngine();

  const updateCustomField = (name: string, value: string): void => {
    props.onUpdateApplicationCustomField({ customFieldName: name, formattedValue: value, valueType: 'value' });
  }

  const updateCustomFieldCalculatedValue = (name: string, calculatedValue: string): void => {
    props.onUpdateApplicationCustomField({ customFieldName: name, formattedValue: calculatedValue, valueType: 'calculatedValue' });
  }

  const updateCustomFieldIsAutomaticallyCalculated = (name: string, value: boolean): void => {
    props.onUpdateApplicationCustomField({ customFieldName: name, formattedValue: value, valueType: 'isAutomaticallyCalculated' });
  }

  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', overflowY: 'auto', overflowX: 'hidden', height: 'calc(100vh - 112px)' }}>
      <>
        {props.lockedByPostStatus && <LockNotice message={props.lockedMessage ?? POSTED_APP_LOCK_TOOLTIP} />}
        <CardHeader
          title={props.title}
        />
        <Table size="small" style={{ marginBottom: '50px' }}>
          <TableHead>
            <TableRow>
              <TableCell width={'600px'}>Field</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Calculated Value</TableCell>
              {props.showScoreColumn && (
                <TableCell>Score</TableCell>
              )}
              <TableCell>Automatic</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!props.isBureauPage && props.entityCustomFields ?
              sort((left: IEntityCustomField, right: IEntityCustomField) => {
                return (left?.customField?.sortOrder ?? 0)
                  < (right?.customField?.sortOrder ?? 0) ? -1 : 1
              }, props.entityCustomFields).filter(c => c.customField.isCustomerCustomField === false).map(f => {
                const isSelect = (f.customField.customFieldOptions?.length || 0) > 0;
                return (
                  <TableRow key={f.id}>
                    <TableCell>
                      {f.customField.name}
                      {f.customField.description &&
                        <Tooltip title={f.customField.description}
                          style={{maxWidth: '500px'}}
                        >
                          <InfoIcon fontSize="small" color="primary"
                            style={{
                              marginLeft: '12px',
                              marginBottom: "-5px",
                            }}
                          />
                        </Tooltip>
                      }
                    </TableCell>
                    <TableCell>
                      <TextField id={f.id}
                        value={f.value ?? ""}
                        onChange={(e) => {
                          updateCustomField(f.customField.name, isSelect ? e.target.value : e.currentTarget.value);
                        }}
                        select={isSelect}
                        disabled={props.lockedByPostStatus || f.customField.isReadonly || (f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                      >
                        <MenuItem value="">&nbsp;</MenuItem>
                        {f.customField.customFieldOptions?.map((o, key) => (
                          <MenuItem key={key} value={o.name}>
                            {o.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {(f.customField.isAutomaticallyCalculated) &&
                        <TextField
                          value={f.calculatedValue ?? ""}
                          onChange={(e) => {
                            updateCustomFieldCalculatedValue(f.customField.name, e.currentTarget.value);
                          }}
                          disabled={props.lockedByPostStatus || (f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                          placeholder={f.customField.placeholder}
                        />
                      }
                    </TableCell>
                    {props.showScoreColumn && (
                      <TableCell>
                        {f.customField.customFieldOptions?.find(x => x.name === f.value)?.score}
                      </TableCell>
                    )}
                    <TableCell>
                      {f.customField.isAutomaticallyCalculated &&
                        <Switch
                          checked={f.isAutomaticallyCalculated}
                          onChange={(e) => {
                            updateCustomFieldIsAutomaticallyCalculated(f.customField.name, e.target.checked);
                          }}
                          color="primary"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                          disabled={props.lockedByPostStatus || f.customField.isReadonly}
                        />
                      }
                    </TableCell>
                  </TableRow>
                )
              }) :
              props.isBureauPage && props.entityCustomFields ?
                sort((left: IEntityCustomField, right: IEntityCustomField) => {
                  return (left?.customField?.sortOrder ?? 0)
                    < (right?.customField?.sortOrder ?? 0) ? -1 : 1
                }, props.entityCustomFields).map(f => {
                  const isSelect = (f.customField.customFieldOptions?.length || 0) > 0;
                  return (
                    <TableRow key={f.id}>
                      <TableCell>
                        {f.customField.name}
                        {f.customField.description &&
                          <Tooltip title={f.customField.description}
                            style={{maxWidth: '500px'}}
                          >
                            <InfoIcon fontSize="small" color="primary"
                              style={{
                                marginLeft: '12px',
                                marginBottom: "-5px",
                              }}
                            />
                          </Tooltip>
                        }
                      </TableCell>
                      <TableCell>
                        <TextField id={f.id}
                          value={f.value}
                          onChange={(e) => {
                            updateCustomField(f.customField.name, isSelect ? e.target.value : e.currentTarget.value);
                          }}
                          select={isSelect}
                          disabled={f.customField.isReadonly ||
                            (f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                        >
                          <MenuItem value="">&nbsp;</MenuItem>
                          {f.customField.customFieldOptions?.map((o, key) => (
                            <MenuItem key={key} value={o.name}>
                              {o.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {(f.customField.isAutomaticallyCalculated) &&
                          <TextField
                            value={f.calculatedValue}
                            onChange={(e) => {
                              updateCustomFieldCalculatedValue(f.customField.name, e.currentTarget.value);
                            }}
                            disabled={(f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                            placeholder={f.customField.placeholder}
                          />
                        }
                      </TableCell>
                      {props.showScoreColumn && (
                        <TableCell>
                          {f.customField.customFieldOptions?.find(x => x.name === f.value)?.score}
                        </TableCell>
                      )}
                      <TableCell>
                        {f.customField.isAutomaticallyCalculated &&
                          <Switch
                            checked={f.isAutomaticallyCalculated}
                            onChange={(e) => {
                              updateCustomFieldIsAutomaticallyCalculated(f.customField.name, e.target.checked);
                            }}
                            color="primary"
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                            disabled={f.customField.isReadonly}
                          />
                        }
                      </TableCell>
                    </TableRow>
                  )
                })
                : null}
          </TableBody>
        </Table>
      </>
      {primary && primary.customer.customerCustomFields && primary.customer.customerCustomFields.length > 0 && (
        <>
          <CardHeader
            title={`Applicant (${primary.customer.firstName} ${primary.customer.lastName})`}
          />
          <Table size="small" style={{ marginBottom: '50px' }}>
            <TableHead>
              <TableRow>
                <TableCell width={'600px'}>Field</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Calculated Value</TableCell>
                {props.showScoreColumn && (
                  <TableCell>Score</TableCell>
                )}
                <TableCell>Automatic</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {primary.customer.customerCustomFields ?
                sort((left: IEntityCustomField, right: IEntityCustomField) => {
                  return (left?.customField?.sortOrder ?? 0)
                    < (right?.customField?.sortOrder ?? 0) ? -1 : 1
                }, primary.customer.customerCustomFields).map(f => {
                  const isSelect = (f.customField.customFieldOptions?.length || 0) > 0;
                  return (
                    <TableRow key={f.id}>
                      <TableCell>
                        {f.customField.name}
                        {f.customField.description &&
                          <Tooltip title={f.customField.description}
                            style={{ maxWidth: '500px' }}
                          >
                            <InfoIcon fontSize="small" color="primary"
                              style={{
                                marginLeft: '12px',
                                marginBottom: "-5px",
                              }}
                            />
                          </Tooltip>
                        }
                      </TableCell>
                      <TableCell>
                        <TextField id={f.id}
                          value={f.value}
                          onChange={(e) => {
                            updateCustomerCustomFields(primary.customer.id, [{
                              formattedValue: isSelect ? e.target.value : e.currentTarget.value,
                              customFieldName: f.customField.name, valueType: 'value'
                            }], currentApp!, dispatch, businessRulesEngine);
                          }}
                          select={isSelect}
                          disabled={f.customField.isReadonly || (f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                        >
                          <MenuItem value="">&nbsp;</MenuItem>
                          {f.customField.customFieldOptions?.map((o, key) => (
                            <MenuItem key={key} value={o.name}>
                              {o.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {(f.customField.isAutomaticallyCalculated) &&
                          <TextField
                            value={f.calculatedValue}
                            onChange={(e) => {
                              updateCustomerCustomFields(primary.customer.id, [{
                                formattedValue: e.currentTarget.value,
                                customFieldName: f.customField.name, valueType: 'calculatedValue'
                              }], currentApp!, dispatch, businessRulesEngine);
                            }}
                            disabled={(f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                            placeholder={f.customField.placeholder}
                          />
                        }
                      </TableCell>
                      {props.showScoreColumn && (
                        <TableCell>
                          {f.customField.customFieldOptions?.find(x => x.name === f.value)?.score}
                        </TableCell>
                      )}
                      <TableCell>
                        {f.customField.isAutomaticallyCalculated &&
                          <Switch
                            checked={f.isAutomaticallyCalculated}
                            onChange={(e) => {
                              updateCustomerCustomFields(primary.customer.id, [{
                                formattedValue: e.target.checked,
                                customFieldName: f.customField.name, valueType: 'isAutomaticallyCalculated'
                              }], currentApp!, dispatch, businessRulesEngine);
                            }}
                            color="primary"
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                            disabled={f.customField.isReadonly}
                          />
                        }
                      </TableCell>
                    </TableRow>
                  )
                }) : null}
            </TableBody>
          </Table>
        </>
      )}
      {cobuyer1 && cobuyer1.customer.customerCustomFields && cobuyer1.customer.customerCustomFields.filter.length > 0 && (
        <>
          <CardHeader
            title={`Co-buyer (${cobuyer1.customer.firstName} ${cobuyer1.customer.lastName})`}
          />
          <Table size="small" style={{ marginBottom: '50px' }}>
            <TableHead>
              <TableRow>
                <TableCell width={'600px'}>Field</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Calculated Value</TableCell>
                {props.showScoreColumn && (
                  <TableCell>Score</TableCell>
                )}
                <TableCell>Automatic</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cobuyer1.customer.customerCustomFields ?
                sort((left: IEntityCustomField, right: IEntityCustomField) => {
                  return (left?.customField?.sortOrder ?? 0)
                    < (right?.customField?.sortOrder ?? 0) ? -1 : 1
                }, cobuyer1.customer.customerCustomFields).map(f => {
                  const isSelect = (f.customField.customFieldOptions?.length || 0) > 0;
                  return (
                    <TableRow key={f.id}>
                      <TableCell>
                        {f.customField.name}
                        {f.customField.description &&
                          <Tooltip title={f.customField.description}
                            style={{ maxWidth: '500px' }}
                          >
                            <InfoIcon fontSize="small" color="primary"
                              style={{
                                marginLeft: '12px',
                                marginBottom: "-5px",
                              }}
                            />
                          </Tooltip>
                        }
                      </TableCell>
                      <TableCell>
                        <TextField id={f.id}
                          value={f.value}
                          onChange={(e) => {
                            updateCustomerCustomFields(cobuyer1.customer.id, [{
                              formattedValue: isSelect ? e.target.value : e.currentTarget.value,
                              customFieldName: f.customField.name, valueType: 'value'
                            }], currentApp!, dispatch, businessRulesEngine);
                          }}
                          select={isSelect}
                          disabled={f.customField.isReadonly ||
                            (f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                        >
                          <MenuItem value="">&nbsp;</MenuItem>
                          {f.customField.customFieldOptions?.map((o, key) => (
                            <MenuItem key={key} value={o.name}>
                              {o.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {(f.customField.isAutomaticallyCalculated) &&
                          <TextField
                            value={f.calculatedValue}
                            onChange={(e) => {
                              updateCustomerCustomFields(cobuyer1.customer.id, [{
                                formattedValue: e.currentTarget.value,
                                customFieldName: f.customField.name, valueType: 'calculatedValue'
                              }], currentApp!, dispatch, businessRulesEngine);
                            }}
                            disabled={(f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                            placeholder={f.customField.placeholder}
                          />
                        }
                      </TableCell>
                      {props.showScoreColumn && (
                        <TableCell>
                          {f.customField.customFieldOptions?.find(x => x.name === f.value)?.score}
                        </TableCell>
                      )}
                      <TableCell>
                        {f.customField.isAutomaticallyCalculated &&
                          <Switch
                            checked={f.isAutomaticallyCalculated}
                            onChange={(e) => {
                              updateCustomerCustomFields(cobuyer1.customer.id, [{
                                formattedValue: e.target.checked,
                                customFieldName: f.customField.name, valueType: 'isAutomaticallyCalculated'
                              }], currentApp!, dispatch, businessRulesEngine);
                            }}
                            color="primary"
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                            disabled={f.customField.isReadonly}
                          />
                        }
                      </TableCell>
                    </TableRow>
                  )
                }) : null}
            </TableBody>
          </Table>
        </>
      )}
      {cobuyer2 && cobuyer2.customer.customerCustomFields && cobuyer2.customer.customerCustomFields.length > 0 && (
        <>
          <CardHeader
            title={`Co-buyer (${cobuyer2.customer.firstName} ${cobuyer2.customer.lastName})`}
          />
          <Table size="small" style={{ marginBottom: '50px' }}>
            <TableHead>
              <TableRow>
                <TableCell width={'600px'}>Field</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Calculated Value</TableCell>
                {props.showScoreColumn && (
                  <TableCell>Score</TableCell>
                )}
                <TableCell>Automatic</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cobuyer2.customer.customerCustomFields ?
                sort((left: IEntityCustomField, right: IEntityCustomField) => {
                  return (left?.customField?.sortOrder ?? 0)
                    < (right?.customField?.sortOrder ?? 0) ? -1 : 1
                }, cobuyer2.customer.customerCustomFields).map(f => {
                  const isSelect = (f.customField.customFieldOptions?.length || 0) > 0;
                  return (
                    <TableRow key={f.id}>
                      <TableCell>
                        {f.customField.name}
                        {f.customField.description &&
                          <Tooltip title={f.customField.description}
                            style={{ maxWidth: '500px' }}
                          >
                            <InfoIcon fontSize="small" color="primary"
                              style={{
                                marginLeft: '12px',
                                marginBottom: "-5px",
                              }}
                            />
                          </Tooltip>
                        }
                      </TableCell>
                      <TableCell>
                        <TextField id={f.id}
                          value={f.value}
                          onChange={(e) => {
                            updateCustomerCustomFields(cobuyer2.customer.id, [{
                              formattedValue: isSelect ? e.target.value : e.currentTarget.value,
                              customFieldName: f.customField.name, valueType: 'value'
                            }], currentApp!, dispatch, businessRulesEngine);
                          }}
                          select={isSelect}
                          disabled={f.customField.isReadonly ||
                            (f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                        >
                          <MenuItem value="">&nbsp;</MenuItem>
                          {f.customField.customFieldOptions?.map((o, key) => (
                            <MenuItem key={key} value={o.name}>
                              {o.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        {(f.customField.isAutomaticallyCalculated) &&
                          <TextField
                            value={f.calculatedValue}
                            onChange={(e) => {
                              updateCustomerCustomFields(cobuyer2.customer.id, [{
                                formattedValue: e.currentTarget.value,
                                customFieldName: f.customField.name, valueType: 'calculatedValue'
                              }], currentApp!, dispatch, businessRulesEngine);
                            }}
                            disabled={(f.isAutomaticallyCalculated && f.customField.isAutomaticallyCalculated)}
                            placeholder={f.customField.placeholder}
                          />
                        }
                      </TableCell>
                      {props.showScoreColumn && (
                        <TableCell>
                          {f.customField.customFieldOptions?.find(x => x.name === f.value)?.score}
                        </TableCell>
                      )}
                      <TableCell>
                        {f.customField.isAutomaticallyCalculated &&
                          <Switch
                            checked={f.isAutomaticallyCalculated}
                            onChange={(e) => {
                              updateCustomerCustomFields(cobuyer2.customer.id, [{
                                formattedValue: e.target.checked,
                                customFieldName: f.customField.name, valueType: 'isAutomaticallyCalculated'
                              }], currentApp!, dispatch, businessRulesEngine);
                            }}
                            color="primary"
                            inputProps={{ 'aria-label': 'primary checkbox' }}
                            disabled={f.customField.isReadonly}
                          />
                        }
                      </TableCell>
                    </TableRow>
                  )
                }) : null}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  )
}

export default CustomFieldForm
